/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { globalRef } from '@amazon/vinyl-util'
import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { globalData } from '@amzn/bf-playback'

// ── Current-view context provider ─────────────────────────────────────────────
//
// A single source of truth for "what the user is looking at" that the chat
// agent threads onto every turn. Two shapes, split by cost:
//
//   - THIN (`ViewContext`): route + primary entity (id/type) + a short
//     descriptor. Pushed on EVERY agent turn (`AgentTurnContext.view`) so the
//     LLM can disambiguate "add chopin" while on a playlist without first
//     having to realize it's missing context. Deliberately small — no visible
//     item list rides here.
//   - HEAVY (`ViewContextDetail`): the full visible-item list / deep data,
//     resolved LAZILY only when the model calls the `get_view_context` pull
//     tool. The current page registers a resolver for it.
//
// Placement: this is a PURE low leaf module — it imports only
// the observable and global-state primitives; nothing imports back into it. Both feeds push in by
// INJECTION so this leaf never imports upward: the router feeds the per-route
// baseline (`router/viewContextRouterFeed` calls `setRouteBaseline` on
// navigate), and the playback façade feeds the current track
// (`blackbolt/nowPlayingViewContextFeed` calls `setNowPlaying`), both wired at
// boot. Importing the router or `blackbolt/` here would drag the router's
// load-time `history` access — and close a back-edge — into every
// `agentSchema`/`blackbolt` consumer. `blackbolt/` (turn builder + transport
// type) and `agentSchema/` (pull getter) depend DOWNWARD on it; it must never
// import `blackbolt/`, `agentSchema/`, the router, or a feature page.

/** The primary on-screen entity the agent should target by default. */
export interface ViewContextEntity {
    /** Catalog entity id (ASIN / playlist id). */
    readonly id: string
    /** Entity kind (`playlist`, `album`, `artist`, `track`). */
    readonly type: string
}

/**
 * A context-specific thing the agent can act on for the current view. Pages
 * set these when the on-screen entity supports an agent action beyond the
 * evergreen default (e.g. an owned playlist the agent can edit). Client-only:
 * consumed by UI surfaces that tailor themselves to the view (starter-prompt
 * chips); the wire builders curate their own `view-*` fields and ignore it.
 */
export type ViewCapability = 'edit-playlist'

/**
 * The track the shared player currently has loaded, as the agent sees it. Lets
 * the model answer "what is this?" / "add this to a playlist" / "more like this"
 * with the current track as the target. Orthogonal to the page/route context —
 * it survives navigation, since something can be playing on any screen.
 */
export interface ViewContextNowPlaying {
    /** Catalog track id (ASIN) — the actionable target for "add this …". */
    readonly id: string
    readonly title: string
    readonly artist: string
    readonly album?: string
    /** Playing vs. paused (a loaded-but-paused track is still context). */
    readonly isPlaying: boolean
    /** True for a video entry; absent for audio. */
    readonly isVideo?: boolean
}

/**
 * The THIN per-turn view context. Cheap to serialize; pushed every turn. The
 * `route` is always present (the current path); `primaryEntity` and a richer
 * `descriptor` are filled in by the page when it has them.
 */
export interface ViewContext {
    /** Current route path (e.g. `/playlists/<id>`). */
    readonly route: string
    /** The primary entity on screen, when the page identifies one. */
    readonly primaryEntity?: ViewContextEntity
    /** Short "what's on screen" phrase for the model (e.g. `Playlist "Jazz"`). */
    readonly descriptor: string
    /**
     * Context-specific agent actions the current view supports. Client-only
     * hint used to tailor UI (starter-prompt chips) to what the agent can
     * actually do here; empty/absent on ordinary views.
     */
    readonly capabilities?: readonly ViewCapability[]
    /**
     * What the shared player currently has loaded, when anything is playing.
     * Fed globally from the playback façade (survives navigation), so the agent
     * can act on the current track from any screen — not just now-playing.
     */
    readonly nowPlaying?: ViewContextNowPlaying
}

/** One visible item, as the model sees it in the HEAVY pull payload. */
export interface ViewContextItem {
    readonly id: string
    readonly type: string
    readonly title: string
    readonly subtitle?: string
}

/** The HEAVY detail a page resolves on demand for the pull tool. */
export interface ViewContextDetail {
    /** The items currently visible on screen, in display order. */
    readonly items: readonly ViewContextItem[]
}

/** What a page hands the provider to enrich the thin context (route is kept). */
export interface ViewContextUpdate {
    readonly primaryEntity?: ViewContextEntity
    readonly descriptor: string
    readonly capabilities?: readonly ViewCapability[]
}

/** Resolves the current page's heavy detail; registered alongside the update. */
export type ViewContextDetailResolver = () => Promise<ViewContextDetail>

function defaultDescriptor(route: string): string {
    if (route === '/' || route === '/feed') return 'Home feed'
    if (route.startsWith('/search')) return 'Search'
    if (route.startsWith('/my/library') || route.startsWith('/library')) return 'Your library'
    // Entity/browse pages set their own richer descriptor on mount; until then
    // the bare route is a usable-enough hint.
    return route
}

function baselineFor(route: string): ViewContext {
    return { route, descriptor: defaultDescriptor(route) }
}

// Route/page context and now-playing are tracked SEPARATELY: the former resets
// on every navigation, the latter follows the shared player across routes. The
// published `current$` is always their composition (`publish`), so a navigation
// can't clobber the current track and a track change can't clobber the page.
const viewStateRef = globalRef<{
    route: ViewContext
    nowPlaying: ViewContextNowPlaying | null
    detailResolver: ViewContextDetailResolver | null
}>(() => ({
    route: baselineFor(window.location.pathname || '/'),
    nowPlaying: null,
    detailResolver: null,
}))

const current$ = globalData(() => data<ViewContext>(viewStateRef.value.route))

/** Recompose the published thin context from the route + now-playing slices. */
function publish(): void {
    const { route, nowPlaying } = viewStateRef.value
    current$.value = {
        ...route,
        ...(nowPlaying ? { nowPlaying } : null),
    }
}

/**
 * Reset to a route-only baseline: a page that doesn't populate still pushes a
 * sensible route context, and the previous page's entity + detail resolver are
 * cleared so they can't leak onto the next route. Called by the router feed
 * (`router/viewContextRouterFeed`) on every navigation — injected at boot so
 * this leaf never imports the router.
 */
export function setRouteBaseline(route: string): void {
    viewStateRef.value.route = baselineFor(route)
    viewStateRef.value.detailResolver = null
    publish()
}

/**
 * Enrich the current view context with the active page's primary entity +
 * descriptor, and register its heavy-detail resolver for the pull tool. The
 * route is preserved from the navigation baseline, so pages never restate it.
 * Cleared automatically on the next navigation.
 */
export function setViewContext(
    update: ViewContextUpdate,
    detail?: ViewContextDetailResolver
): void {
    viewStateRef.value.route = {
        route: viewStateRef.value.route.route,
        ...(update.primaryEntity ? { primaryEntity: update.primaryEntity } : null),
        descriptor: update.descriptor,
        ...(update.capabilities && update.capabilities.length > 0
            ? { capabilities: update.capabilities }
            : null),
    }
    viewStateRef.value.detailResolver = detail ?? null
    publish()
}

/**
 * Update (or clear, with `null`) the current-track slice. Fed globally from the
 * playback façade (`blackbolt/nowPlayingViewContextFeed`) so the agent knows
 * what's playing on any route, not just the now-playing screen. Survives
 * navigation — only a track change or stop clears it.
 */
export function setNowPlaying(next: ViewContextNowPlaying | null): void {
    viewStateRef.value.nowPlaying = next
    publish()
}

/** The current thin view context — read by the turn builder each turn. */
export function currentViewContext(): ViewContext {
    return current$.value
}

/** Reactive thin view context, for surfaces that want to track it live. */
export const viewContext$: ObservableValue<ViewContext> = current$

/**
 * Resolve the current page's heavy detail (visible items), or null when no
 * page has registered a resolver. Invoked by the `get_view_context` pull tool.
 */
export async function resolveViewContextDetail(): Promise<ViewContextDetail | null> {
    return viewStateRef.value.detailResolver ? viewStateRef.value.detailResolver() : null
}
